/**
 * AssignCreatorForm
 *
 * Modal form the Org uses to invite a specific Creator to assign one of
 * their Live Rooms to one of the Org's Show Pages. Mirrors the spec's
 * "STEP 1: Organization Initiates Assignment" wireframe (UPDATE.md §3.6.2).
 *
 * Inputs (react-final-form):
 *   - creatorSearch (FieldTextInput) — typeahead; onChange dispatches
 *     onSearchCreators. The parent renders the result list outside the
 *     form's value model; clicking a row writes the picked creator's id
 *     into a hidden Field via form.change.
 *   - liveRoomId (FieldSelect) — populated once a creator is picked.
 *   - showPageId (FieldSelect) — the Org's own shows.
 *   - startDate / endDate (FieldSingleDatePicker).
 *   - hasEndDate (FieldCheckbox).
 *   - consentXxx (FieldCheckbox × 3).
 *
 * Submit is disabled until a creator is picked, a live room is chosen,
 * a show is chosen, a start date is set, at least one consent is checked,
 * and (if the assignment is closed-ended) an end date is set.
 */

import React from 'react';
import { arrayOf, bool, func, object, shape, string } from 'prop-types';
import { Form as FinalForm, Field } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import {
  Avatar,
  Form,
  FieldTextInput,
  FieldSelect,
  FieldCheckbox,
  FieldSingleDatePicker,
  IconSpinner,
  SecondaryButton,
  PrimaryButton,
} from '../../components';

import css from './AssignCreatorForm.module.css';

const CONSENTS = [
  { id: 'viewInsights', labelId: 'AssignCreatorForm.consent.viewInsights' },
  { id: 'publishEpisodes', labelId: 'AssignCreatorForm.consent.publishEpisodes' },
  { id: 'temporaryAudienceAccess', labelId: 'AssignCreatorForm.consent.temporaryAudienceAccess' },
];

const identity = v => v;
const TODAY = new Date();

const consentFieldName = key => `consent${key.charAt(0).toUpperCase()}${key.slice(1)}`;

// Stable reference — passing an inline object would make FinalForm
// re-initialise (and wipe in-progress input) on every parent re-render
// triggered by the typeahead's redux updates.
const INITIAL_VALUES = {
  hasEndDate: true,
  startDate: { date: TODAY },
  consentViewInsights: true,
  consentPublishEpisodes: true,
  consentTemporaryAudienceAccess: true,
};

const AssignCreatorFormComponent = props => {
  const {
    onSubmit,
    onCancel,
    onSearchCreators,
    onPickCreator,
    creatorOptions,
    searchInProgress,
    liveRoomOptions,
    liveRoomsLoading,
    showOptions,
    showsLoading,
    submitInProgress,
    submitError,
    intl,
  } = props;

  return (
    <FinalForm
      onSubmit={onSubmit}
      initialValues={INITIAL_VALUES}
      keepDirtyOnReinitialize
      render={fp => {
        const { handleSubmit, values, invalid, form, pristine } = fp;

        const noConsents = !CONSENTS.some(c => values[consentFieldName(c.id)]);

        // End date must be at (or after) the start date. Pull both
        // values so we can both grey out unreachable dates in the
        // picker AND keep submit disabled if someone bypasses.
        const startMs = values.startDate?.date ? new Date(values.startDate.date).getTime() : null;
        const endMs = values.endDate?.date ? new Date(values.endDate.date).getTime() : null;
        const endBeforeStart =
          values.hasEndDate && startMs != null && endMs != null && endMs < startMs;

        const submitDisabled =
          submitInProgress ||
          invalid ||
          !values.creatorId ||
          !values.liveRoomId ||
          !values.showPageId ||
          !values.startDate?.date ||
          (values.hasEndDate && !values.endDate?.date) ||
          endBeforeStart ||
          noConsents ||
          pristine;

        // Reset the picked creator if the user edits the query, and
        // dispatch the typeahead search. Called from <OnChange> below
        // so FieldTextInput keeps full control of its own value (no
        // dropped keystrokes) — same pattern as TopbarSearchForm.
        //
        // Important: pickCreator writes the picked label into
        // creatorSearch so the input visually fills in. That write
        // also fires this listener; the `nextValue === creatorLabel`
        // check below tells us "this came from a pick, leave it alone".
        const handleCreatorSearchValue = nextValue => {
          const justPicked = !!values.creatorId && nextValue === values.creatorLabel;
          if (justPicked) return;
          if (values.creatorId) {
            form.change('creatorId', undefined);
            form.change('creatorLabel', undefined);
            form.change('creatorUser', undefined);
            form.change('liveRoomId', undefined);
            form.change('liveRoomTitle', undefined);
          }
          onSearchCreators(nextValue || '');
        };

        const pickCreator = opt => {
          // Fill the input with the picked label so the user sees the
          // selection inline. The OnChange listener below is told to
          // treat this specific write as "just picked, leave alone"
          // via the nextValue === creatorLabel check. We also stash the
          // raw user object so we can render their avatar inside the
          // search field while the pick is active.
          form.change('creatorId', opt.id);
          form.change('creatorLabel', opt.displayName);
          form.change('creatorSearch', opt.displayName);
          form.change('creatorUser', opt.user || null);
          form.change('liveRoomId', undefined);
          form.change('liveRoomTitle', undefined);
          onPickCreator(opt.id);
        };

        // FieldSelect's onChange prop is called with the raw value
        // (not a DOM event) — see FieldSelect.js handleChange.
        const handleLiveRoomChange = value => {
          const picked = (liveRoomOptions || []).find(l => l.id === value);
          form.change('liveRoomTitle', picked?.title || null);
        };

        const handleShowChange = value => {
          const picked = (showOptions || []).find(s => s.id === value);
          form.change('showPageTitle', picked?.title || null);
        };

        const showResults =
          !values.creatorId &&
          (values.creatorSearch || '').trim().length > 0 &&
          (creatorOptions || []).length > 0;

        const showEmptyState =
          !values.creatorId &&
          (values.creatorSearch || '').trim().length > 0 &&
          !searchInProgress &&
          (creatorOptions || []).length === 0;

        return (
          <Form className={css.form} onSubmit={handleSubmit}>
            {/* Hidden fields the search UI writes into. */}
            <Field name="creatorId" component="input" type="hidden" />
            <Field name="creatorLabel" component="input" type="hidden" />
            <Field name="liveRoomTitle" component="input" type="hidden" />
            <Field name="showPageTitle" component="input" type="hidden" />

            {/* ── Creator search ── */}
            <div className={css.searchBlock}>
              <div
                className={classNames(css.searchInputWrap, {
                  [css.searchInputWrapPicked]: !!values.creatorId,
                })}
              >
                {values.creatorId && values.creatorUser ? (
                  <span className={css.searchAvatar} aria-hidden>
                    <Avatar
                      className={css.searchAvatarInner}
                      user={values.creatorUser}
                      disableProfileLink
                      useCreatorAvatar
                    />
                  </span>
                ) : null}
                <FieldTextInput
                  id="creatorSearch"
                  name="creatorSearch"
                  type="text"
                  autoComplete="off"
                  label={intl.formatMessage({ id: 'AssignCreatorForm.findCreator' })}
                  placeholder={intl.formatMessage({
                    id: 'AssignCreatorForm.findCreatorPlaceholder',
                  })}
                />
                <OnChange name="creatorSearch">{handleCreatorSearchValue}</OnChange>
                {searchInProgress ? (
                  <span className={css.searchSpinner} aria-hidden>
                    <IconSpinner />
                  </span>
                ) : null}
              </div>
              {showResults ? (
                <ul className={css.results}>
                  {creatorOptions.map(opt => (
                    <li key={opt.id}>
                      <button
                        type="button"
                        className={css.resultRow}
                        onClick={() => pickCreator(opt)}
                      >
                        <Avatar
                          className={css.resultAvatar}
                          user={opt.user}
                          disableProfileLink
                          useCreatorAvatar
                        />
                        <span className={css.resultText}>
                          <span className={css.resultName}>{opt.displayName}</span>
                          {opt.email ? <span className={css.resultEmail}>{opt.email}</span> : null}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {showEmptyState ? (
                <span className={css.searchHint}>
                  <FormattedMessage id="AssignCreatorForm.noCreatorMatches" />
                </span>
              ) : null}
            </div>

            {/* ── Live Room ── */}
            <FieldSelect
              id="liveRoomId"
              name="liveRoomId"
              label={intl.formatMessage({ id: 'AssignCreatorForm.liveRoom' })}
              disabled={!values.creatorId || liveRoomsLoading}
              onChange={handleLiveRoomChange}
            >
              <option value="">
                {liveRoomsLoading
                  ? intl.formatMessage({ id: 'AssignCreatorForm.loadingLiveRooms' })
                  : !values.creatorId
                  ? intl.formatMessage({ id: 'AssignCreatorForm.pickCreatorFirst' })
                  : (liveRoomOptions || []).length === 0
                  ? intl.formatMessage({ id: 'AssignCreatorForm.noLiveRooms' })
                  : intl.formatMessage({ id: 'AssignCreatorForm.pickLiveRoom' })}
              </option>
              {(liveRoomOptions || []).map(l => (
                <option key={l.id} value={l.id}>
                  {l.title}
                </option>
              ))}
            </FieldSelect>

            {/* ── Show ── */}
            <FieldSelect
              id="showPageId"
              name="showPageId"
              label={intl.formatMessage({ id: 'AssignCreatorForm.assignToPage' })}
              disabled={showsLoading}
              onChange={handleShowChange}
            >
              <option value="">
                {showsLoading
                  ? intl.formatMessage({ id: 'AssignCreatorForm.loadingPages' })
                  : (showOptions || []).length === 0
                  ? intl.formatMessage({ id: 'AssignCreatorForm.noPages' })
                  : intl.formatMessage({ id: 'AssignCreatorForm.pickPage' })}
              </option>
              {(showOptions || []).map(s => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </FieldSelect>

            {/* ── Dates ── */}
            <div className={css.dateRow}>
              <FieldSingleDatePicker
                id="startDate"
                name="startDate"
                label={intl.formatMessage({ id: 'AssignCreatorForm.startDate' })}
                placeholderText={intl.formatDate(TODAY, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
                format={identity}
                parse={identity}
                useMobileMargins={false}
                isOutsideRange={() => false}
              />
              <FieldSingleDatePicker
                id="endDate"
                name="endDate"
                label={intl.formatMessage({ id: 'AssignCreatorForm.endDate' })}
                placeholderText={intl.formatDate(TODAY, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
                format={identity}
                parse={identity}
                useMobileMargins={false}
                disabled={!values.hasEndDate}
                // Grey out any day strictly before the chosen start date. The picker
                // passes each day as a Date at local midnight.
                isOutsideRange={day => {
                  if (startMs == null) return false;
                  const startOfStart = new Date(startMs);
                  startOfStart.setHours(0, 0, 0, 0);
                  return day < startOfStart;
                }}
              />
            </div>
            {endBeforeStart ? (
              <p className={css.errorText}>
                <FormattedMessage id="AssignCreatorForm.endBeforeStart" />
              </p>
            ) : null}
            <FieldCheckbox
              id="hasEndDateOpenEnded"
              name="openEnded"
              value="open-ended"
              label={intl.formatMessage({ id: 'AssignCreatorForm.noEndDate' })}
              onChange={e => {
                const checked = e.target.checked;
                form.change('hasEndDate', !checked);
                if (checked) form.change('endDate', undefined);
              }}
            />

            {/* ── Consents ── */}
            <div className={css.consentList}>
              <span className={css.fieldLabel}>
                <FormattedMessage id="AssignCreatorForm.requireConsents" />
              </span>
              {CONSENTS.map(c => {
                const name = consentFieldName(c.id);
                return (
                  <FieldCheckbox
                    key={c.id}
                    id={name}
                    name={name}
                    value={c.id}
                    label={intl.formatMessage({ id: c.labelId })}
                  />
                );
              })}
              {noConsents ? (
                <p className={css.errorText}>
                  <FormattedMessage id="AssignCreatorForm.consentsRequired" />
                </p>
              ) : null}
            </div>

            {submitError ? (
              <p className={css.errorText}>
                <FormattedMessage
                  id="AssignCreatorForm.submitFailed"
                  values={{ message: submitError.message || '' }}
                />
              </p>
            ) : null}

            <div className={css.actions}>
              <SecondaryButton type="button" onClick={onCancel}>
                <FormattedMessage id="AssignCreatorForm.cancel" />
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={submitDisabled} inProgress={submitInProgress}>
                <FormattedMessage id="AssignCreatorForm.submit" />
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

AssignCreatorFormComponent.defaultProps = {
  creatorOptions: [],
  liveRoomOptions: [],
  showOptions: [],
  searchInProgress: false,
  liveRoomsLoading: false,
  showsLoading: false,
  submitInProgress: false,
  submitError: null,
};

AssignCreatorFormComponent.propTypes = {
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  onCancel: func.isRequired,
  onSearchCreators: func.isRequired,
  onPickCreator: func.isRequired,
  creatorOptions: arrayOf(shape({ id: string.isRequired, displayName: string, email: string })),
  searchInProgress: bool,
  liveRoomOptions: arrayOf(shape({ id: string.isRequired, title: string })),
  liveRoomsLoading: bool,
  showOptions: arrayOf(shape({ id: string.isRequired, title: string })),
  showsLoading: bool,
  submitInProgress: bool,
  submitError: object,
};

const AssignCreatorForm = injectIntl(AssignCreatorFormComponent);
export default AssignCreatorForm;
